import { ExternalLink, Lock } from 'lucide-react'

import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { Button } from '@/customer/components/ui/button'

/**
 * Dashed-border spinner shown while a payment is being confirmed or
 * reconciled.
 */
export function PaymentProcessing() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-6">
      <LoadingSpinner />
      <p className="text-sm text-muted-foreground">Processing payment…</p>
    </div>
  )
}

/**
 * Primary payment CTA. Adapts to three states:
 *
 * - `clientSecret` present: in-app pay button + optional "Pay on
 *   Stripe instead" fallback when `hostedInvoiceUrl` is also set.
 * - No `clientSecret`, `hostedInvoiceUrl` present: full-width
 *   "Pay invoice on Stripe" button.
 * - Neither: renders nothing.
 */
export function PayButton({
  clientSecret,
  hostedInvoiceUrl,
  /**
   * label is ignored when clientSecret is null and hostedInvoiceUrl
   * is the only remaining course of action
   */
  label,
  disabled,
  onClick,
}: {
  clientSecret: string | null
  hostedInvoiceUrl: string | null
  label: string
  disabled?: boolean
  onClick: () => void
}) {
  if (clientSecret) {
    return (
      <div className="flex flex-col gap-4">
        <Button type="button" onClick={onClick} disabled={disabled} className="h-14 w-full">
          <Lock className="size-5" />
          {label}
        </Button>

        {hostedInvoiceUrl && (
          <button
            type="button"
            onClick={() => window.open(hostedInvoiceUrl, '_blank', 'noopener,noreferrer')}
            className="inline-flex items-center justify-center gap-1.5 text-label-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
          >
            <ExternalLink className="size-3.5" />
            Having trouble? Pay on Stripe instead.
          </button>
        )}
      </div>
    )
  }

  if (hostedInvoiceUrl) {
    return (
      <Button
        type="button"
        onClick={() => window.open(hostedInvoiceUrl, '_blank', 'noopener,noreferrer')}
        className="h-14 w-full"
      >
        <ExternalLink className="size-5" />
        Pay invoice on Stripe
      </Button>
    )
  }

  return null
}

/**
 * Inline "View on Stripe" link for invoice cards.
 */
export function InvoiceStripeLink({ url }: { url: string }) {
  return (
    <button
      type="button"
      onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
      className="inline-flex items-center gap-1.5 text-label-sm font-semibold text-foreground underline underline-offset-4 hover:text-foreground/70 cursor-pointer transition-colors"
    >
      <ExternalLink className="size-3.5" />
      View on Stripe
    </button>
  )
}
